import { Check } from 'lucide-react'
import { useState } from 'react'

import type { ReplyPolicy } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Menu } from '@/components/ui/Menu'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'

import { REPLY_POLICY_OPTIONS, replyPolicyOption } from '../replyPolicy'

function OptionList({
  value,
  onPick,
}: {
  value: ReplyPolicy
  onPick: (value: ReplyPolicy) => void
}) {
  const t = useT()
  return (
    <div role="radiogroup" aria-label={t('replyPolicy.title')}>
      {REPLY_POLICY_OPTIONS.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onPick(option.value)}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-[15px] font-bold hover:bg-surface-hover"
          >
            <span className="flex size-9 shrink-0 items-center justify-center bg-brand text-on-brand px-corners [--px-step:2px]">
              <option.icon className="size-4" />
            </span>
            <span className="flex-1">{t(option.label)}</span>
            {active && <Check className="size-4 text-brand-ink" aria-hidden />}
          </button>
        )
      })}
    </div>
  )
}

/** Composer 里的小按钮：显示当前设置，点开选。 */
export function ReplyPolicyPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: ReplyPolicy
  onChange: (value: ReplyPolicy) => void
  disabled?: boolean
}) {
  const t = useT()
  const current = replyPolicyOption(value)

  return (
    <Menu
      align="left"
      trigger={({ toggle }) => (
        <button
          type="button"
          onClick={toggle}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[13px] font-bold text-brand-ink hover:bg-brand-ink/10 disabled:opacity-50"
        >
          <current.icon className="size-3.5" />
          {t(current.summary)}
        </button>
      )}
    >
      {(close) => (
        <div className="w-72">
          <p className="px-4 pt-3 text-[15px] font-extrabold">{t('replyPolicy.title')}</p>
          <p className="px-4 pb-2 text-[13px] text-ink-muted">{t('replyPolicy.hint')}</p>
          <OptionList
            value={value}
            onPick={(next) => {
              onChange(next)
              close()
            }}
          />
        </div>
      )}
    </Menu>
  )
}

/** 发出之后改设置（作者菜单里的「修改谁可以回复」）。 */
export function ReplyPolicyDialog({
  open,
  initial,
  saving,
  onClose,
  onSave,
}: {
  open: boolean
  initial: ReplyPolicy
  saving: boolean
  onClose: () => void
  onSave: (value: ReplyPolicy) => void
}) {
  const t = useT()
  const [value, setValue] = useState<ReplyPolicy>(initial)

  return (
    <Dialog open={open} onClose={onClose} title={t('replyPolicy.title')}>
      <div className="pb-4">
        <p className="px-4 pb-3 text-[15px] text-ink-muted">{t('replyPolicy.hint')}</p>
        <OptionList value={value} onPick={setValue} />
        <div className={cn('flex justify-end px-4 pt-3')}>
          <Button onClick={() => onSave(value)} loading={saving} disabled={value === initial}>
            {t('replyPolicy.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
